import { useEffect, useRef } from "react";
import { Button, Color, Flex, Icon, Text } from "src/elements";
import { Transition } from "src/utils";
import styled from "styled-components";

import { useToasts } from "../store/hooks";
import { Toast as ToastType } from "../store/types";
import { TimerBar } from "./TimerBar";
import React from "react";

const ToastElement = styled(Flex)<{ state: ToastType["state"] }>`
  flex-direction: column;
  width: 300px;
  background: ${Color.white};
  box-shadow: rgba(22, 22, 22, 0.15) 0px 10px 25px;
  border-radius: 3px;
  overflow: hidden;
  transform-origin: top;
  animation: ${({ state }) => `${state === "OPEN" ? "open" : "close"} ${Transition.slow} forwards`};

  @keyframes open {
    0% {
      opacity: 0;
      height: 0;
      transform: translateX(-500%);
    }

    100% {
      opacity: 1;
      height: auto;
      transform: translateX(0);
      margin-top: 10px;
    }
  }

  @keyframes close {
    0% {
      opacity: 1;
      height: auto;
      transform: translateY(0);
    }

    100% {
      opacity: 0;
      height: 0;
      transform: translateY(50%);
    }
  }
`;

interface Props {
  toast: ToastType;
}

export const ToastComponent = ({
  toast: { id, title, message, type, state, actions, duration },
}: Props) => {
  const { closeToast } = useToasts();

  const refTimeout = useRef<NodeJS.Timeout>(null);

  // Effect to automatically close the toast.
  useEffect(() => {
    if (duration !== "INFINITE") {
      refTimeout.current = setTimeout(() => {
        closeToast(id);
      }, duration);
    }
  }, []);

  const onClickClose = () => {
    if (refTimeout.current) {
      clearTimeout(refTimeout.current);
    }
    closeToast(id);
  };

  return (
    <ToastElement state={state}>
      <Flex flexGrow padding="7px" align="start" gap="7px">
        <Flex>
          <Icon
            name={
              (type === "SUCCESS" && "toastSuccess") ||
              (type === "WARNING" && "toastWarning") ||
              (type === "ERROR" && "toastError")
            }
            color={
              (type === "SUCCESS" && Color.success) ||
              (type === "WARNING" && Color.yellow) ||
              (type === "ERROR" && Color.red)
            }
          />
        </Flex>

        <Flex flexGrow minWidth="0">
          <Flex direction="column" gap="5px" flexGrow>
            <Text variant="body1" color={Color.textSecondary}>
              {title}
            </Text>

            <Flex>
              <Text
                variant="caption1"
                color={Color.textSecondary}
                wordBreak="break-all"
                lineClamp={6}
              >
                {message}
              </Text>
            </Flex>
          </Flex>
        </Flex>

        <Flex
          minWidth="14px"
          minHeight="14px"
          justify="center"
          align="center"
          onClick={onClickClose}
        >
          <Icon name="close" size={10} color={Color.spaceGray} />
        </Flex>
      </Flex>

      {actions.length > 0 && (
        <Flex padding="7px" justify="right" gap="7px">
          {actions.map((action, i) => (
            <Button
              key={i}
              color={
                (type === "SUCCESS" && "success") ||
                (type === "WARNING" && "yellow") ||
                (type === "ERROR" && "red")
              }
              size="small"
              onClick={action.onClick}
            >
              {action.label}
            </Button>
          ))}
        </Flex>
      )}

      <TimerBar type={type} duration={duration} />
    </ToastElement>
  );
};

export const Toast = React.memo(ToastComponent);
